import React from 'react'
import { Col, Row, message, Button } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
const { REACT_APP_API_BASE_URL } = process.env

const ViewBidSecurities: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation().state
  const userInfo = JSON.parse(localStorage.getItem('userInfo') ?? '{}')

  const handleDownloadFile = async (fileName: string): Promise<any> => {
    await fetch(`${REACT_APP_API_BASE_URL as string}/bid-security/download-file/${fileName}`,
      {
        headers: {
          'Content-Disposition': `attachment; filename=${fileName}`,
          Authorization: userInfo.token
        }
      })
      .then(async (response) => await response.blob())
      .then((blob) => {
        const file = new Blob([blob], { type: 'application/pdf' })
        window.open(URL.createObjectURL(file))
      })
      .catch(() => {
        void message.error('Error fetching PDF:')
      })
  }

  return (
    <div className="w-full p-6 bg-gray-50">
      {/* Enhanced Custom CSS Styles */}
      <style>{
        `
        .enhanced-card {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .enhanced-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
        .enhanced-button {
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .enhanced-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .section-header {
          background: var(--refund-theme-primary-soft);
          color: #111827;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }
        `
      }</style>

      {/* Enhanced Breadcrumb Navigation */}
      <div className="px-5 py-4 border-b border-gray-300" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '0 0 12px 12px',
        marginBottom: '20px'
      }}>
        <div className="flex justify-between items-center">
          <div className="text-title font-semibold text-gray-800 flex items-center">
            <div style={{
              background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              boxShadow: '0 4px 8px rgba(55, 118, 47, 0.3)'
            }}>
              <span style={{ fontSize: '18px' }}>🛡️</span>
            </div>
            <div>
              <span
                className="cursor-pointer text-blue-600 hover:text-blue-700 enhanced-button"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => { navigate('/bid-securities-list') }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)'
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)'
                }}
              >
                🔙 Back to Bid Securities List
              </span>
              <span className="mx-3 text-gray-400">›</span>
              <span className="font-medium text-gray-600" style={{
                background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: '600'
              }}>{location?.full_name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Bidder Details Section */}
      <div className="px-7 py-4 mt-4 enhanced-card" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease'
      }}>
        <div className="section-header">
          <span style={{ fontSize: '18px' }}>👤</span>
          Bidder Details
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={8}>
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            <div className="font-semibold text-gray-700">Bidder's ID Number</div>
            <div className="text-base font-medium text-gray-500">{location?.nid}</div>
          </Col>
          <Col xs={24} xl={8}>
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            <div className="font-semibold text-gray-700">Bidder's Name</div>
            <div className="text-base font-medium text-gray-500">{location?.full_name}</div>
          </Col>
        </Row>
        <Row gutter={[16, 16]} className="pt-4">
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Phone Number</div>
            <div className="text-base font-medium text-gray-500">{location?.phone_number}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Email Address</div>
            <div className="text-base font-medium text-gray-500">{location?.email}</div>
          </Col>
        </Row>
      </div>

      {/* Enhanced Auction Details Section */}
      <div className="px-7 py-4 mt-6 enhanced-card" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease'
      }}>
        <div className="section-header">
          <span style={{ fontSize: '18px' }}>🏛️</span>
          Auction Details
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Execution Case Number</div>
            <div className="text-base font-medium text-gray-500">{location?.execution_case_no}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Property ID</div>
            <div className="text-base font-medium text-gray-500">{location?.property_id}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Expiration Date</div>
            <div className="text-base font-medium text-gray-500">
              {moment(location?.expiration_date).format('YYYY-MM-DD hh:mm')}
            </div>
          </Col>
        </Row>
        <Row gutter={[16, 16]} className="pt-4">
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Bid Name</div>
            <div className="text-base font-medium text-gray-500">{location?.name}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Bid Security Amount</div>
            <div className="text-base font-medium text-gray-500">
              {parseInt(String(location?.security_amount)).toLocaleString()}
            </div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Status</div>
            <div className="text-base font-medium text-gray-500">{location?.status}</div>
          </Col>
        </Row>
      </div>

      {/* Enhanced Bid Security Supplied Section */}
      <div className="px-7 py-4 mt-6 enhanced-card" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease'
      }}>
        <div className="section-header">
          <span style={{ fontSize: '18px' }}>🛡️</span>
          Bid Security Supplied
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Bid Security Type</div>
            <div className="text-base font-medium text-gray-500">{location?.type}</div>
          </Col>
          {location?.type !== 'PAYMENT' && (
            <>
              <Col xs={24} xl={8}>
                <div className="font-semibold text-gray-700">Download Document</div>
                <Button
                  type="link"
                  className="enhanced-button"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                    border: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
                  onClick={async () => await handleDownloadFile(location?.file)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  <i className="material-icons align-middle mr-2">cloud_download</i>📥 Click to Download
                </Button>
              </Col>
              <Col xs={24} xl={8}>
                <div className="font-semibold text-gray-700">Document Valid Until</div>
                <div className="text-base font-medium text-gray-500">
                  {moment(location?.expiration_date).format('YYYY-MM-DD hh:mm')}
                </div>
              </Col>
              <Col xs={24}>
                <div className="font-semibold text-gray-700">Added on</div>
                <div className="text-base font-medium text-gray-500">
                  {moment(location?.created_at).format('YYYY-MM-DD hh:mm:ss')}
                </div>
              </Col>
            </>
          )}
        </Row>
      </div>

      {/* Enhanced Payment Details Section */}
      <div className="px-7 py-4 mt-6 enhanced-card" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease'
      }}>
        <div className="section-header">
          <span style={{ fontSize: '18px' }}>💳</span>
          Payment Details
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Bank Name</div>
            <div className="text-base font-medium text-gray-500">{location?.bank_name}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Account Number</div>
            <div className="text-base font-medium text-gray-500">{location?.bank_account}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Slip Number</div>
            <div className="text-base font-medium text-gray-500">
              {location?.slip_number?.trim().length > 0 ? location?.slip_number : 'N/A'}
            </div>
          </Col>
        </Row>
        <Row gutter={[16, 16]} className="pt-4">
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Payment Channel</div>
            <div className="text-base font-medium text-gray-500">{location?.payment_channel}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Currency</div>
            <div className="text-base font-medium text-gray-500">{location?.currency}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Amount</div>
            <div className="text-base font-medium text-gray-500">
              {parseInt(String(location?.amount)).toLocaleString()}
            </div>
          </Col>
        </Row>
        <Row gutter={[16, 16]} className="pt-4">
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Merchant Code</div>
            <div className="text-base font-medium text-gray-500">{location?.merchant_code}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Payment Channel Name</div>
            <div className="text-base font-medium text-gray-500">{location?.payment_channel_name}</div>
          </Col>
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Transaction ID</div>
            <div className="text-base font-medium text-gray-500">{location?.id}</div>
          </Col>
        </Row>
        <Row gutter={[16, 16]} className="pt-4">
          <Col xs={24} xl={8}>
            <div className="font-semibold text-gray-700">Payment Date</div>
            <div className="text-base font-medium text-gray-500">
              {moment(location?.payment_date_time).format('YYYY-MM-DD')}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default ViewBidSecurities
